// src/api/client.js
import axios from "axios";
import {
  notifySessionExpired,
  resolveSessionRealm,
  shouldHandleSessionExpired,
} from "../utils/sessionExpired";
import { getDiningGuestId } from "../utils/diningGuestId";
import {
  getActiveGoogleUsageHeaders,
  setGoogleUsageTransport,
} from "../utils/googleApiUsageTracker";

// In dev with proxy: use "" so requests go to same origin (avoids cross-origin cookie issues)
// In prod: use full API URL
// Netlify branch/PR + empty API_URL: same-origin `/api/*` (see netlify.toml proxy) so CSRF cookies work.
// `replace(/\/+$/, "")` defends against a trailing slash in the Netlify env var (e.g. `https://api/`)
// which combined with relative paths like "/api/uploadImage" would yield "//api/uploadImage" and 404.
const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const SOCKET_URL = (process.env.REACT_APP_SOCKET_URL || "").replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// CSRF token (your existing logic)
let csrfToken = null;
export const setCsrfToken = (token) => (csrfToken = token);
export const getCsrfToken = () => csrfToken;

const fetchAndSetCsrfToken = async () => {
  const res = await api.get("/api/csrf-token");
  const token = res.data?.csrfToken;
  if (token) setCsrfToken(token);
  return token;
};

/** Call before login POST to ensure fresh token + cookie. Fixes "invalid csrf token" on first login. */
export const ensureCsrfToken = fetchAndSetCsrfToken;

api.interceptors.request.use((config) => {
  const guestId = getDiningGuestId();
  if (guestId) config.headers["X-Dining-Guest-Id"] = guestId;
  const usageHeaders = getActiveGoogleUsageHeaders();
  const url = String(config.url || "");
  if (
    usageHeaders &&
    (url.includes("/api/maps/geocode") ||
      url.includes("/api/maps/reverse-geocode") ||
      url.includes("/api/smart-route/") ||
      url.includes("/coming-soon"))
  ) {
    config.headers = { ...(config.headers || {}), ...usageHeaders };
  }
  if (config.method && config.method.toUpperCase() !== "GET") {
    if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
  }
  return config;
});

// Detect when API returns HTML instead of JSON (wrong server / misconfigured proxy)
const looksLikeHtml = (data) => {
  if (typeof data !== "string") return false;
  const trimmed = data.trim().toLowerCase();
  return trimmed.startsWith("<!doctype") || trimmed.startsWith("<html");
};

// ✅ normalize errors + detect HTML responses + auto-retry on invalid CSRF
api.interceptors.response.use(
  (res) => {
    const data = res?.data;
    if (data && looksLikeHtml(data)) {
      const err = new Error(
        "API returned HTML instead of JSON. Check REACT_APP_API_URL and ensure your API host (e.g. api.lememeprix.fr) points to your backend."
      );
      err.response = res;
      err.code = "HTML_RESPONSE";
      return Promise.reject(err);
    }
    return res;
  },
  async (error) => {
    // Network / CORS / server down
    if (!error.response) {
      return Promise.reject({
        status: "error",
        code: "NETWORK_ERROR",
        message: "Network error. Please check connection or server.",
        errors: [],
        httpStatus: 0,
        raw: error,
      });
    }

    const { status, data, config } = error.response;
    const extractApiMessage = (payload) => {
      if (payload == null) return "Request failed";
      if (typeof payload === "string") return payload;
      if (typeof payload !== "object") return String(payload);
      if (typeof payload.stripeMessage === "string" && payload.stripeMessage.trim()) {
        return payload.stripeMessage;
      }
      if (typeof payload.message === "string" && payload.message.trim()) {
        return payload.message;
      }
      if (typeof payload.msg === "string" && payload.msg.trim()) {
        return payload.msg;
      }
      if (typeof payload.error === "string" && payload.error.trim()) {
        return payload.error;
      }
      if (payload.error && typeof payload.error === "object") {
        if (typeof payload.error.message === "string" && payload.error.message.trim()) {
          return payload.error.message;
        }
        if (typeof payload.error.msg === "string" && payload.error.msg.trim()) {
          return payload.error.msg;
        }
      }
      return "Request failed";
    };

    const rawMessage = extractApiMessage(data);
    const msg = String(rawMessage).toLowerCase();
    const isCsrfError = status === 403 && (msg.includes("csrf") || data?.code === "EBADCSRFTOKEN" || data?.error?.code === "EBADCSRFTOKEN");

    // Auto-retry once on 403 invalid CSRF (no manual refresh needed)
    if (isCsrfError && !config.__csrfRetried) {
      config.__csrfRetried = true;
      try {
        await fetchAndSetCsrfToken();
        return api.request(config);
      } catch (_) {
        // Retry failed, fall through to reject
      }
    }

    if (shouldHandleSessionExpired(config, status, rawMessage)) {
      notifySessionExpired({
        realm: resolveSessionRealm(
          typeof window !== "undefined" ? window.location.pathname : ""
        ),
        from: typeof window !== "undefined" ? window.location.pathname : "",
      });
    }

    // Your backend standard format (prefer stripeMessage from Stripe so UI is not stuck on generic data.error)
    if (data && typeof data === "object") {
      return Promise.reject({
        status: data.status || "error",
        code: data.code || data.error?.code || data.reason || "HTTP_ERROR",
        message: rawMessage,
        stripeMessage: data.stripeMessage,
        reason: data.reason,
        errors: Array.isArray(data.errors) ? data.errors : [],
        httpStatus: status,
        raw: error,
      });
    }

    // Fallback if backend returned plain text
    const plainMessage = String(data || "Request failed");
    if (shouldHandleSessionExpired(config, status, plainMessage)) {
      notifySessionExpired({
        realm: resolveSessionRealm(
          typeof window !== "undefined" ? window.location.pathname : ""
        ),
        from: typeof window !== "undefined" ? window.location.pathname : "",
      });
    }

    return Promise.reject({
      status: "error",
      code: "HTTP_ERROR",
      message: plainMessage,
      errors: [],
      httpStatus: status,
      raw: error,
    });
  }
);

/** Use for socket.io etc. In dev (empty) use same origin so proxy works. */
export const getApiBaseUrl = () => API_URL || (typeof window !== "undefined" ? window.location.origin : "");

/**
 * Socket.io cannot use Netlify’s static `/api` proxy. When `REACT_APP_API_URL` is empty (same-origin REST),
 * set `REACT_APP_SOCKET_URL` to the real API host (e.g. Heroku) for WebSocket connections.
 */
export const getSocketBaseUrl = () => {
  if (typeof window === "undefined") {
    return SOCKET_URL || API_URL || "";
  }
  return SOCKET_URL || API_URL || window.location.origin;
};

/** Read API error code from axios or the normalized interceptor rejection shape. */
export function apiErrorCode(err) {
  const fromAxios = err?.raw?.response?.data?.error;
  if (typeof fromAxios === "string" && fromAxios.trim()) return fromAxios.trim();
  if (typeof err?.code === "string") {
    const code = err.code.trim();
    if (code && !["HTTP_ERROR", "NETWORK_ERROR", "HTML_RESPONSE"].includes(code)) {
      return code;
    }
  }
  const msg = typeof err?.message === "string" ? err.message.trim() : "";
  if (/^[A-Z][A-Z0-9_]+$/.test(msg)) return msg;
  return "";
}

export default api;

setGoogleUsageTransport((payload, { keepalive } = {}) => {
  if (keepalive && typeof fetch === "function") {
    const url = `${API_URL}/api/analytics/google-api-usage`;
    fetch(url, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
    return;
  }
  api.post("/api/analytics/google-api-usage", payload).catch(() => {});
});
